import { AssistChip, Button, Stack, Text } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IParsedOrganizationUserRoles } from '~/helpers/parseOrganizationUserRoles';
import type { IApiGetOrganizationUserResponseBody } from '~/lib/api/modules/Organizations';
import { UserAvatar } from '~/components/atoms/UserAvatar';
import { Table } from '~/components/molecules/Table';
import { getOrganizationRoleLabel } from '~/components/organisms/fields/OrganizationRoleSelector';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { useAuthUser } from '~/lib/auth';

export type IOrganizationUserListItemProps = {
  organizationId: string;
  user: IApiGetOrganizationUserResponseBody;
  roles?: IParsedOrganizationUserRoles;
};

const styles = stylex.create({
  me: {
    fontWeight: 'bold',
  },
});

export const OrganizationUserListItem: React.FC<
  IOrganizationUserListItemProps
> = (props) => {
  const { organizationId, user, roles } = props;
  const userInfo = parseUserInfo(user);
  const { t } = useTranslation();
  const { fullName, emailAddress } = userInfo;
  const authUser = useAuthUser();
  const isMe = authUser?._id === user._id;

  return (
    <Table.Row>
      <Stack
        as={Table.BodyAction}
        horizontal
        gap={3}
        href={
          roles?.atLeastAdmin
            ? `/organizations/${organizationId}/users/${user._id}`
            : undefined
        }
        sx={isMe && styles.me}
      >
        <UserAvatar user={user} />
        <Stack gap={1}>
          <Text variant="title" size="sm" truncate>
            {fullName}
            {isMe ? ` (${t('common.you')})` : null}
          </Text>
          <Text variant="body" size="sm" dimmed>
            {emailAddress}
          </Text>
        </Stack>
      </Stack>
      <Stack as={Table.Body} horizontal gap={2}>
        {user.roles?.map((role, roleIndex) => (
          <AssistChip
            key={roleIndex}
            label={getOrganizationRoleLabel(role, t) ?? role}
          />
        ))}
      </Stack>
      <Stack as={Table.Body} horizontal justify="end">
        {roles?.atLeastAdmin ? (
          <Button
            variant="text"
            href={`/organizations/${organizationId}/users/${user._id}`}
          >
            {t('common.edit')}
          </Button>
        ) : null}
      </Stack>
    </Table.Row>
  );
};
